import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay, takeUntil, withLatestFrom } from 'rxjs/operators';

import { optionalTranslate } from '@language';
import { EnumValue } from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import { FormRadioListItem } from '../../../../../form-controls/entities/form-radio-list-item.entity';
import { FormSelectOption } from '../../../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../../../form-controls/entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../../../form-controls/entities/multi-form-select-options.entity';
import { multiFormSelectOptionWrapper } from '../../../../../form-controls/helpers/multi-form-select-options-wrapper-helper';
import { BatchFieldReadHelper } from '../../../../helpers/fields/batch-field-read-helper';
import { FieldReadHelper } from '../../../../helpers/fields/field-read.helper';
import { ShSimpleInputFieldComponent } from '../simple-input-field/simple-input-field.component';

@Component({
    standalone: false,
    selector: 'sh-enum-field',
    templateUrl: './enum-input-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShEnumInputFieldComponent extends ShSimpleInputFieldComponent {
    public selectOptions$!: Observable<MultiFormSelectOptions>;
    public radioOptions$!: Observable<FormRadioListItem[]>;
    public searchTerm$: BehaviorSubject<string> = new BehaviorSubject('');

    constructor(protected fieldHelper: FieldReadHelper) {
        super(fieldHelper);
        this.initOptionStreams();
    }

    initOptionStreams(): void {
        const possibleValues$: Observable<EnumValue[]> = this.profileField$.pipe(
            map(profileField => profileField.FieldDefinition.Values),
            filter((val): val is EnumValue[] => typeof val !== 'undefined' && Array.isArray(val)),
            shareReplay(1)
        );

        this.selectOptions$ = possibleValues$.pipe(
            withLatestFrom(this.profileField$),
            map(([possibleValues, profileField]) =>
                possibleValues.map(
                    enumValue =>
                        ({
                            label: optionalTranslate(
                                this.translateService,
                                profileField.FieldDefinition.FlatKey,
                                enumValue.Value.Label
                            ),
                            value: enumValue.Value.Label,
                            disabled: !enumValue.Active,
                            disabledInfoMsg: this.translateService.instant('form.enum.obsolete-value'),
                            allowUnsetDisabled: true
                        }) as FormSelectOption
                )
            ),
            withLatestFrom(this.searchTerm$),
            map(([possibleValues, searchTerm]) => {
                const options: FormSelectOption[] = possibleValues.filter(value => value.label.includes(searchTerm));
                return multiFormSelectOptionWrapper(options);
            })
        );

        this.radioOptions$ = possibleValues$.pipe(
            withLatestFrom(this.profileField$),
            map(([possibleValues, profileField]) =>
                possibleValues.map(enumValue => ({
                    id: enumValue.Value.Label,
                    value: enumValue.Value.Label,
                    label: optionalTranslate(
                        this.translateService,
                        profileField.FieldDefinition.FlatKey,
                        enumValue.Value.Label
                    ),
                    disabled: !enumValue.Active,
                    disabledInfoMsg: this.translateService.instant('form.enum.obsolete-value')
                }))
            )
        );
    }

    init(profileField: ExtendedBaseProfileField, records: PartialRecord[], control: UntypedFormControl): void {
        const values = this.isBatchEdit
            ? BatchFieldReadHelper.getBatchFieldValue(profileField.FieldDefinition)
            : this.fieldHelper.getMergedEnumFieldValue(profileField.FieldDefinition, records);

        control.setValue(values);

        this.initValueListener(control);
    }

    private initValueListener(control: UntypedFormControl): void {
        // MED-2317 Can't clear single select fields without setting a null value
        control.valueChanges
            .pipe(
                filter((changes: FormValue<string>[]) => changes.length > 1 || !!changes[0]?.value),
                takeUntil(this.destroy$)
            )
            .subscribe(changes => {
                const checkIsSet: (value: FormValue<string>) => boolean = value => value.state === FormValueState.SET;
                const checkIsIndeterminate: (value: FormValue<string>) => boolean = value =>
                    value.state === FormValueState.INDETERMINATE;
                if (!changes.find(value => checkIsSet(value) || checkIsIndeterminate(value))) {
                    control.setValue([new FormValue(null, FormValueState.UNSET)]);
                }
            });
    }

    onSearchTermChange(searchTerm: string): void {
        this.searchTerm$.next(searchTerm);
    }
}
